import axios from 'axios';
import { Project } from '../types/Project';
import { ResetPassword } from '../AuthenticationProvider';
import { CreateUser } from '../UsersProvider';
import { CreateTeamMember } from '../TeamMembersProvider';

export async function makeAPICall(
  verb: string,
  path: string,
  data: any,
  token: string | null = null
) {
  let config: any = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (token) {
    config.headers.Authorization = token;
  }
  let request;

  try {
    switch (verb) {
      case 'get':
      default: {
        request = await axios.get(path, config);
        break;
      }

      case 'delete': {
        request = await axios.delete(path, config);
        break;
      }

      case 'post': {
        request = await axios.post(path, data, config);
        break;
      }

      case 'put':
        request = await axios.put(path, data, config);
        break;
    }

    return request.data;
  } catch (error) {
    handleApiError(error);
  }
}

function handleApiError(error: any) {
  console.log(error);
  // If 401 delete token
  if (error.response.status === 401) {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('userInfo');
  }

  throw error;
}

export async function getProjectIndex(token: string) {
  return await makeAPICall('get', '/api/projects?per_page=100', null, token);
}

export async function createProject(project: Project, token: string) {
  return await makeAPICall('post', '/api/projects', project, token);
}

export async function updateProject(project: Project, token: string) {
  return await makeAPICall(
    'put',
    `/api/projects/${project.id}`,
    project,
    token
  );
}

export async function deleteProject(projectId: number, token: string) {
  return await makeAPICall('delete', `/api/projects/${projectId}`, null, token);
}

export async function getUsersIndex(token: string) {
  return await makeAPICall('get', '/api/users?per_page=9999', null, token);
}

export async function resetPassword(values: ResetPassword) {
  return await makeAPICall('post', '/api/reset_password', values, null);
}

export async function inviteUser(values: CreateUser, token: string) {
  return await makeAPICall('post', '/api/invite_user', { user: values }, token);
}

export async function deleteUser(userId: number, token: string) {
  return await makeAPICall('delete', `/api/users/${userId}`, null, token);
}

export async function updateUser(user: CreateUser, token: string) {
  return await makeAPICall(
    'put',
    `/api/users/${user.id}`,
    { user: user },
    token
  );
}

export async function getTeam(projectId: number, token: string) {
  return await makeAPICall('get', `/api/teams/${projectId}?per_page=9999`, null, token);
}

export async function forgotPassword(email: string) {
  return await makeAPICall(
    'post',
    '/api/forgot_password',
    { forgot_password: { email: email } },
    null
  );
}

export async function createTeamMember(
  values: CreateTeamMember,
  token: string
) {
  return await makeAPICall('post', '/api/team_members', values, token);
}

export async function deleteTeamMember(teamMemberId: number, token: string) {
  return await makeAPICall(
    'delete',
    `/api/team_members/${teamMemberId}`,
    null,
    token
  );
}

export async function updateTeamMember(
  teamMember: CreateTeamMember,
  token: string
) {
  return await makeAPICall(
    'put',
    `/api/team_members/${teamMember.team_member.id}`,
    teamMember,
    token
  );
}

export async function getResourcesStatus(projectId: number, token: string) {
  return await makeAPICall(
    'get',
    `/api/resources/by_project/${projectId}/status`,
    null,
    token
  );
}

export async function getProject(projectId: number, token: string) {
  return await makeAPICall('get', `/api/projects/${projectId}`, null, token);
}

export async function getProjectResources(projectId: number, token: string) {
  return await makeAPICall(
    'get',
    `/api/resources/by_project/${projectId}`,
    null,
    token
  );
}

export async function getResourceActions(projectId: number, token: string) {
  return await makeAPICall(
    'get',
    `/api/resource_actions/${projectId}`,
    null,
    token
  );
}

export async function getConfigActions(projectId: number, token: string) {
  return await makeAPICall(
    'get',
    `/api/config_actions/${projectId}`,
    null,
    token
  );
}

export async function setRemoteRepository(
  projectId: number,
  url: string,
  token: string
) {
  return await makeAPICall(
    'post',
    `/api/projects/${projectId}/add_remote`,
    { project: { remote_repository_url: url } },
    token
  );
}

export async function publish(
  projectId: number,
  message: string,
  token: string
) {
  return await makeAPICall(
    'put',
    `/api/projects/${projectId}/publish`,
    { project: { message: message } },
    token
  );
}

export async function getResourceByGuid(guid: string, token: string) {
  return await makeAPICall('get', `/api/resources/${guid}`, null, token);
}

export async function saveProjectImage(
  projectId: number,
  image: FormData,
  token: string
) {
  return await makeAPICall(
    'post',
    `/api/projects/${projectId}/set_project_image`,
    image,
    token
  );
}

export async function quickDeleteResource(
  projectId: number,
  resourceGuid: string,
  token: string
) {
  return await makeAPICall(
    'post',
    '/api/resource_management/quick_delete',
    {
      project_id: projectId,
      resource_guid: resourceGuid
    },
    token
  )
}

export async function abandonResource(
  projectId: number,
  resourceGuid: string,
  token: string
) {
  return await makeAPICall(
    'post',
    '/api/resource_management/abandon',
    {
      project_id: projectId,
      resource_guid: resourceGuid
    },
    token
  )
}
